/**
 * SESSION 147e — Confluence Tab
 *
 * Combines AlphaScout fundamental score + SmartFlow ML p_up into a
 * single 15-stock equal-weight portfolio with conviction tiers (S/A/B).
 *
 * Two panels:
 *   1. Live Picks — current 15 stocks ranked by combined_rank
 *   2. Backtest    — walk-forward with the same combine logic
 */

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Activity, Loader2, Play, RefreshCw, Sparkles, GitMerge, Target,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

const Pill = ({ tone = 'neutral', children, testId }) => {
  const palette = {
    success: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
    warn: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
    danger: 'bg-rose-500/15 text-rose-300 ring-rose-500/30',
    info: 'bg-sky-500/15 text-sky-300 ring-sky-500/30',
    neutral: 'bg-zinc-500/15 text-zinc-300 ring-zinc-500/30',
    accent: 'bg-violet-500/15 text-violet-300 ring-violet-500/30',
    fuchsia: 'bg-fuchsia-500/15 text-fuchsia-300 ring-fuchsia-500/30',
  };
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${palette[tone] || palette.neutral}`}>
      {children}
    </span>
  );
};

const Stat = ({ label, value, tone = 'neutral', testId }) => {
  const tint = {
    success: 'text-emerald-300',
    warn: 'text-amber-300',
    danger: 'text-rose-300',
    accent: 'text-fuchsia-300',
    neutral: 'text-zinc-100',
  }[tone];
  return (
    <div className="rounded-md bg-zinc-900/70 border border-zinc-800 px-3 py-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-sm font-mono font-semibold mt-0.5 ${tint}`}>{value}</div>
    </div>
  );
};

const tierTone = (t) => (t === 'S' ? 'fuchsia' : t === 'A' ? 'success' : 'info');

// ---------- Live Picks Panel ----------
const PicksPanel = ({ getAuth }) => {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/api/admin/confluence/picks/current`, { headers: getAuth() });
      setData(r.data || null);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [getAuth]);

  useEffect(() => { refresh(); }, [refresh]);

  const trigger = async () => {
    try {
      setRunning(true);
      const r = await axios.post(`${API}/api/admin/confluence/run`, {}, { headers: getAuth() });
      toast.success(r.data?.message || 'Confluence run started.');
      let polled = 0;
      const id = setInterval(async () => {
        polled += 1;
        await refresh();
        if (polled > 12) clearInterval(id);
      }, 5000);
      setTimeout(() => setRunning(false), 60000);
    } catch (e) {
      toast.error(`Run: ${e?.response?.data?.detail || e.message}`);
      setRunning(false);
    }
  };

  const picks = data?.picks || [];
  const tb = data?.tier_breakdown || {};
  const lr = data?.latest_run;

  return (
    <div className="rounded-xl border border-fuchsia-700/30 bg-fuchsia-950/15 p-4 space-y-3" data-testid="confluence-picks-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
            <GitMerge size={14} className="text-fuchsia-300" /> Live Picks
            <Pill tone="fuchsia">15 stocks · equal weight 6.67%</Pill>
            <Pill tone="accent">tiers: S/A/B</Pill>
          </div>
          <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed max-w-3xl">
            Combines AlphaScout's fundamental composite_score with SmartFlow's ML
            <span className="font-mono mx-1 text-fuchsia-300">p_up</span>
            via rank-merge. Tier S = both ranked top-15 (high agreement);
            tier A = both top-30; tier B = one top-15 + other top-100. Strong disagreement = dropped.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={refresh} disabled={loading} className="rounded-md bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs text-zinc-200 inline-flex items-center gap-1 disabled:opacity-50" data-testid="confluence-refresh">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
          <button onClick={trigger} disabled={running} className="rounded-md bg-fuchsia-600 hover:bg-fuchsia-500 px-3 py-1.5 text-xs text-white font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="confluence-run">
            {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            {running ? 'Running…' : 'Re-rank now'}
          </button>
        </div>
      </div>

      {lr && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <Stat label="Picks" value={data.picks_count || 0} testId="cnf-stat-picks" />
          <Stat label="Tier S" value={tb.S || 0} tone="accent" testId="cnf-stat-S" />
          <Stat label="Tier A" value={tb.A || 0} tone="success" testId="cnf-stat-A" />
          <Stat label="Tier B" value={tb.B || 0} tone="warn" testId="cnf-stat-B" />
          <Stat label="Alpha date" value={lr.alpha_score_date || '—'} testId="cnf-stat-alpha-date" />
          <Stat label="ML date" value={lr.ml_score_date || '—'} testId="cnf-stat-ml-date" />
        </div>
      )}

      <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
        {picks.length === 0 ? (
          <div className="text-zinc-400 text-xs italic px-4 py-6 text-center">
            No picks yet. Click <span className="text-fuchsia-300 font-semibold">Re-rank now</span> to run.
            Requires AlphaScout daily scoring + a trained Phase 4 ML model.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="confluence-picks-table">
              <thead className="bg-zinc-900/80 text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Symbol</th>
                  <th className="px-3 py-2 text-left">Tier</th>
                  <th className="px-3 py-2 text-right">Combined rank</th>
                  <th className="px-3 py-2 text-right">α Score</th>
                  <th className="px-3 py-2 text-right">α Rank</th>
                  <th className="px-3 py-2 text-right">ML p_up</th>
                  <th className="px-3 py-2 text-right">ML Rank</th>
                  <th className="px-3 py-2 text-right">Weight</th>
                  <th className="px-3 py-2 text-right">Price</th>
                </tr>
              </thead>
              <tbody>
                {picks.map((p) => (
                  <tr key={p.symbol} className="border-t border-zinc-800/60" data-testid={`confluence-pick-${p.symbol}`}>
                    <td className="px-3 py-1.5 font-mono text-zinc-500">{p.rank}</td>
                    <td className="px-3 py-1.5 font-mono font-semibold text-zinc-100">{p.symbol}</td>
                    <td className="px-3 py-1.5"><Pill tone={tierTone(p.tier)}>{p.tier}</Pill></td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{p.combined_rank}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{p.alpha_score?.toFixed(1)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-500">#{p.alpha_rank}</td>
                    <td className={`px-3 py-1.5 text-right font-mono ${p.ml_p_up >= 0.30 ? 'text-emerald-300 font-semibold' : p.ml_p_up >= 0.15 ? 'text-amber-300' : 'text-zinc-400'}`}>
                      {(p.ml_p_up * 100).toFixed(1)}%
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-500">#{p.ml_rank}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{p.weight_pct?.toFixed(2)}%</td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{p.price ? `₹${Number(p.price).toLocaleString('en-IN')}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Backtest Panel ----------
const BacktestPanel = ({ getAuth }) => {
  const [latest, setLatest] = useState(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [periodFrom, setPeriodFrom] = useState(daysAgoISO(750));
  const [periodTo, setPeriodTo] = useState(daysAgoISO(7));
  const [holdingDays, setHoldingDays] = useState(20);
  const [sampleEvery, setSampleEvery] = useState(5);
  const [universeFilter, setUniverseFilter] = useState('all');
  const [useRegime, setUseRegime] = useState(true);
  const [minAlpha, setMinAlpha] = useState(0);
  const [mode, setMode] = useState('combined');  // 147g — A/B vs alpha_only
  const [showTrades, setShowTrades] = useState(false);
  const [tradesSortKey, setTradesSortKey] = useState('entry_date');
  const [tradesSortDir, setTradesSortDir] = useState('desc');

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/api/admin/confluence/backtest/latest`, { headers: getAuth() });
      setLatest(r.data?.result || null);
      setRunning(!!r.data?.is_running);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [getAuth]);

  useEffect(() => { refresh(); }, [refresh]);

  const trigger = async () => {
    try {
      setRunning(true);
      const r = await axios.post(`${API}/api/admin/confluence/backtest/run`, {
        period_from: periodFrom, period_to: periodTo,
        holding_days: Number(holdingDays),
        sample_every: Number(sampleEvery),
        universe_filter: universeFilter,
        use_regime_filter: useRegime,
        min_alpha_score: Number(minAlpha),
        mode,
      }, { headers: getAuth() });
      toast.success(r.data?.message || 'Backtest started');
      let polled = 0;
      const id = setInterval(async () => {
        polled += 1;
        await refresh();
        if (polled > 60) clearInterval(id);
      }, 15000);
    } catch (e) {
      toast.error(`Backtest: ${e?.response?.data?.detail || e.message}`);
      setRunning(false);
    }
  };

  return (
    <div className="rounded-xl border border-emerald-700/30 bg-emerald-950/10 p-4 space-y-3" data-testid="confluence-backtest-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
            <Target size={14} className="text-emerald-300" /> Backtest
            <Pill tone="success">walk-forward · daily rebalance</Pill>
          </div>
          <p className="text-[11px] text-zinc-400 mt-1 max-w-3xl leading-relaxed">
            Sweeps history with the same combine logic. Uses a microstructure-derived
            AlphaScout proxy (since live AlphaScout has only 2 dates of persisted score data)
            + the trained Phase 4 ML model. Equal-weight 15-stock portfolio, regime-aware.
          </p>
        </div>
        <button onClick={refresh} disabled={loading} className="rounded-md bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs text-zinc-200 inline-flex items-center gap-1 disabled:opacity-50" data-testid="confluence-bt-refresh">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 items-end">
        <label className="text-xs text-zinc-400 flex flex-col gap-1">From
          <input type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="confluence-bt-from" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">To
          <input type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="confluence-bt-to" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">Holding (days)
          <input type="number" min={1} max={60} value={holdingDays} onChange={(e) => setHoldingDays(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="confluence-bt-hold" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">Sample every
          <input type="number" min={1} max={20} value={sampleEvery} onChange={(e) => setSampleEvery(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="confluence-bt-sample" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">Universe
          <select value={universeFilter} onChange={(e) => setUniverseFilter(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="confluence-bt-universe">
            <option value="all">All real stocks</option>
            <option value="nifty200">Nifty 200</option>
          </select>
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1" title="Min AlphaScout-proxy score to consider; 0 = no floor.">
          Min α score
          <input type="number" min={0} max={100} step={5} value={minAlpha} onChange={(e) => setMinAlpha(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="confluence-bt-min-alpha" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1" title="combined = AlphaScout proxy + ML overlay (rank-merge). alpha_only = top 15 by AlphaScout proxy alone (apples-to-apples vs AlphaScout).">
          Mode
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="confluence-bt-mode">
            <option value="combined">Combined (α + ML)</option>
            <option value="alpha_only">Alpha-only (no ML)</option>
          </select>
        </label>
        <button onClick={trigger} disabled={running} className="rounded-md bg-emerald-600 hover:bg-emerald-500 px-3 py-2 text-xs text-white font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="confluence-bt-run">
          {running ? <Loader2 className="animate-spin" size={12} /> : <Play size={12} />}
          {running ? 'Running…' : 'Run Backtest'}
        </button>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-zinc-400">
        <input type="checkbox" checked={useRegime} onChange={(e) => setUseRegime(e.target.checked)} className="accent-emerald-500" data-testid="confluence-bt-regime" />
        <span>NIFTY &gt; 200DMA gate (recommended ON)</span>
      </div>

      {latest && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 space-y-3" data-testid="confluence-bt-result">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="text-[11px] text-zinc-500 font-mono">{latest.backtest_id}</div>
              <div className="text-xs text-zinc-300 mt-0.5">
                {latest.period_from} → {latest.period_to} · hold {latest.holding_days}d · sample 1/{latest.sample_every} · universe={latest.universe_filter}
                {latest.mode === 'alpha_only' && (
                  <span className="ml-2 text-amber-300 font-semibold">[α-only mode · no ML]</span>
                )}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5 font-mono">
                regime {latest.use_regime_filter ? 'ON' : 'OFF'}
                {' · '}min α≥{latest.min_alpha_score}
                {' · '}skipped {latest.skipped_regime || 0} regime days
                {' · '}avg hold {latest.avg_bars_held}d
              </div>
            </div>
            <Pill tone={latest.status === 'completed' ? 'success' : latest.status === 'running' ? 'info' : 'danger'}>{latest.status}</Pill>
          </div>

          {latest.status === 'completed' && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2">
                <Stat label="Trades" value={latest.total_trades} testId="cnf-bt-stat-trades" />
                <Stat label="Win rate" value={`${latest.win_rate_pct}%`} tone={latest.win_rate_pct >= 55 ? 'success' : latest.win_rate_pct >= 50 ? 'warn' : 'danger'} testId="cnf-bt-stat-win" />
                <Stat label="Avg winner" value={`+${latest.avg_winner_pct}%`} tone="success" testId="cnf-bt-stat-aw" />
                <Stat label="Avg loser" value={`${latest.avg_loser_pct}%`} tone="danger" testId="cnf-bt-stat-al" />
                <Stat label="Expectancy" value={`${latest.expectancy_pct >= 0 ? '+' : ''}${latest.expectancy_pct}%`} tone={latest.expectancy_pct > 0 ? 'success' : 'danger'} testId="cnf-bt-stat-exp" />
                <Stat label="Sharpe" value={latest.sharpe} testId="cnf-bt-stat-sharpe" />
                <Stat label="Max DD" value={`${latest.max_drawdown_pct}%`} tone={latest.max_drawdown_pct < 10 ? 'success' : 'warn'} testId="cnf-bt-stat-dd" />
              </div>

              {latest.by_tier && Object.keys(latest.by_tier).length > 0 && (
                <div className="rounded border border-zinc-800 overflow-hidden">
                  <div className="px-3 py-1.5 bg-zinc-900 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">By tier</div>
                  <table className="w-full text-[11px]">
                    <thead className="bg-zinc-900/60 text-zinc-500 uppercase tracking-wider">
                      <tr>
                        <th className="px-3 py-1.5 text-left">Tier</th>
                        <th className="px-3 py-1.5 text-right">Count</th>
                        <th className="px-3 py-1.5 text-right">Win %</th>
                        <th className="px-3 py-1.5 text-right">Avg PnL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(latest.by_tier).map(([t, v]) => (
                        <tr key={t} className="border-t border-zinc-800/60">
                          <td className="px-3 py-1.5"><Pill tone={tierTone(t)}>{t}</Pill></td>
                          <td className="px-3 py-1.5 text-right font-mono">{v.count}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{v.win_rate_pct?.toFixed(1)}%</td>
                          <td className={`px-3 py-1.5 text-right font-mono ${v.avg_pnl_pct > 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                            {v.avg_pnl_pct >= 0 ? '+' : ''}{v.avg_pnl_pct?.toFixed(2)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* 147g — Trades panel: every selection over the backtest window */}
              {Array.isArray(latest.trades) && latest.trades.length > 0 && (
                <div className="rounded border border-zinc-800 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowTrades(!showTrades)}
                    className="w-full px-3 py-2 bg-zinc-900 hover:bg-zinc-800 text-left text-xs flex items-center justify-between"
                    data-testid="confluence-bt-toggle-trades"
                  >
                    <span className="font-semibold text-zinc-200 uppercase tracking-wider text-[10px]">
                      All Trades ({latest.trades.length}) — every stock the model picked
                    </span>
                    <span className="text-zinc-500 text-[10px]">
                      {showTrades ? '▲ Hide' : '▼ Show'} · unique symbols: {new Set(latest.trades.map(t => t.symbol)).size}
                    </span>
                  </button>
                  {showTrades && (() => {
                    const sortedTrades = [...latest.trades].sort((a, b) => {
                      const va = a[tradesSortKey], vb = b[tradesSortKey];
                      const cmp = (typeof va === 'number' && typeof vb === 'number')
                        ? va - vb
                        : String(va || '').localeCompare(String(vb || ''));
                      return tradesSortDir === 'asc' ? cmp : -cmp;
                    });
                    const ToggleSort = ({ k, label, align = 'left' }) => (
                      <th
                        onClick={() => {
                          if (tradesSortKey === k) setTradesSortDir(tradesSortDir === 'asc' ? 'desc' : 'asc');
                          else { setTradesSortKey(k); setTradesSortDir('desc'); }
                        }}
                        className={`px-3 py-1.5 cursor-pointer hover:text-zinc-300 text-${align}`}
                        data-testid={`confluence-bt-sort-${k}`}
                      >
                        {label}{tradesSortKey === k ? (tradesSortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                      </th>
                    );
                    return (
                      <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="w-full text-[11px]" data-testid="confluence-bt-trades-table">
                          <thead className="bg-zinc-900/80 text-zinc-500 uppercase tracking-wider sticky top-0">
                            <tr>
                              <ToggleSort k="symbol" label="Symbol" />
                              <ToggleSort k="entry_date" label="Entry" />
                              <ToggleSort k="exit_date" label="Exit" />
                              <ToggleSort k="bars_held" label="Days" align="right" />
                              <ToggleSort k="entry_price" label="Entry ₹" align="right" />
                              <ToggleSort k="exit_price" label="Exit ₹" align="right" />
                              <ToggleSort k="pnl_pct" label="PnL %" align="right" />
                              <ToggleSort k="tier" label="Tier" />
                              <ToggleSort k="alpha_score" label="α Score" align="right" />
                              <ToggleSort k="ml_p_up" label="p_up" align="right" />
                            </tr>
                          </thead>
                          <tbody>
                            {sortedTrades.map((t, i) => (
                              <tr key={`${t.symbol}-${t.entry_date}-${i}`} className="border-t border-zinc-800/40 hover:bg-zinc-900/40">
                                <td className="px-3 py-1.5 font-mono font-semibold text-zinc-100">{t.symbol}</td>
                                <td className="px-3 py-1.5 font-mono text-zinc-400">{t.entry_date}</td>
                                <td className="px-3 py-1.5 font-mono text-zinc-400">{t.exit_date}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-zinc-400">{t.bars_held}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-zinc-300">₹{t.entry_price?.toLocaleString('en-IN')}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-zinc-300">₹{t.exit_price?.toLocaleString('en-IN')}</td>
                                <td className={`px-3 py-1.5 text-right font-mono font-semibold ${t.pnl_pct > 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                                  {t.pnl_pct >= 0 ? '+' : ''}{t.pnl_pct?.toFixed(2)}%
                                </td>
                                <td className="px-3 py-1.5"><Pill tone={tierTone(t.tier)}>{t.tier}</Pill></td>
                                <td className="px-3 py-1.5 text-right font-mono text-zinc-400">{t.alpha_score?.toFixed(1)}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-zinc-400">{t.ml_p_up ? (t.ml_p_up * 100).toFixed(1) + '%' : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ---------- Tab root ----------
const ConfluenceTab = () => {
  const getAuth = useCallback(() => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  return (
    <div className="space-y-6" data-testid="confluence-tab">
      <div className="rounded-xl border border-fuchsia-700/40 bg-gradient-to-br from-fuchsia-950/30 to-violet-950/20 p-5">
        <div className="flex items-center gap-2 text-base font-semibold text-zinc-100">
          <Sparkles size={16} className="text-fuchsia-300" />
          Confluence — Combined Model (AlphaScout × SmartFlow)
          <Pill tone="fuchsia">147e</Pill>
        </div>
        <p className="text-[12px] text-zinc-400 mt-2 leading-relaxed max-w-4xl">
          Single 15-stock portfolio, 100% capital, equal-weight. Daily rebalance.
          Uses AlphaScout's fundamental composite (long-term quality) + SmartFlow's
          ML <span className="font-mono text-fuchsia-300">p_up</span> (short-term smart-money) via rank-merge.
          Disagreement is encoded as conviction tiers — no hard exit override.
          Phase C (FII/DII features) lands on top of this.
        </p>
      </div>

      <PicksPanel getAuth={getAuth} />
      <BacktestPanel getAuth={getAuth} />
    </div>
  );
};

export default ConfluenceTab;
